import React from 'react';
import useDisclaimer from '../hooks/useDisclaimer';

/**
 * <ManagedDisclaimer dkey="portal_footer" as="p" className="..." />
 *
 * Renders a disclaimer managed in Admin > Disclaimers. HTML text renders as
 * HTML; plain text keeps blank-line paragraphs. While loading nothing renders;
 * an error renders visibly (no fallback copy).
 *
 * `as` sets the wrapper element. Use as="span" for text inside a sentence:
 * a single-paragraph HTML value is unwrapped so it stays inline.
 */
const ERROR_STYLE = { color: '#F26B43', fontStyle: 'italic' };

const isHtml = (t) => /<[a-z][\s\S]*>/i.test(t);

const unwrapSingleParagraph = (html) => {
  const m = html.trim().match(/^<p[^>]*>([\s\S]*)<\/p>$/i);
  return m && !/<\/p>/i.test(m[1]) ? m[1] : html;
};

const ManagedDisclaimer = ({ dkey, as: Tag = 'div', className, style, portfolioCode, brandDomain }) => {
  const { text, error, loading } = useDisclaimer(dkey, { portfolioCode, brandDomain });

  if (loading) return null;
  if (error) {
    return <Tag className={className} style={{ ...style, ...ERROR_STYLE }}>[{error}]</Tag>;
  }
  if (!text) return null;

  if (isHtml(text)) {
    const html = Tag === 'span' || Tag === 'p' ? unwrapSingleParagraph(text) : text;
    return <Tag className={className} style={style} dangerouslySetInnerHTML={{ __html: html }} />;
  }

  const paras = text.split(/\n\s*\n/);
  if (Tag === 'span' || Tag === 'p' || paras.length === 1) {
    return <Tag className={className} style={{ whiteSpace: 'pre-line', ...style }}>{text}</Tag>;
  }
  return (
    <Tag className={className} style={style}>
      {paras.map((p, i) => <p key={i} style={{ whiteSpace: 'pre-line' }}>{p.trim()}</p>)}
    </Tag>
  );
};

export default ManagedDisclaimer;
